import { format } from "date-fns";
import Papa from "papaparse";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AllLearnersExportRow {
  "Learner Name": string;
  Email: string;
  "User Type": string;
  "Curricula Enrolled": number;
  "Curricula Completed": number;
  "Courses Enrolled": number;
  "Courses Completed": number;
  "Avg Curriculum Progress %": string;
  "Avg Course Progress %": string;
  "Certificates Earned": number;
  "Pending Assignments": number;
  "Completed Assignments": number;
  "Last Activity Date": string;
}

export async function exportAllLearnersCSV(): Promise<void> {
  try {
    // Fetch all profiles
    const { data: profiles, error: profilesError } = await supabase.rpc(
      "admin_search_users_for_deletion",
      {
        search_query: "",
        include_no_name: true,
      }
    );

    if (profilesError) {
      throw profilesError;
    }

    // Fetch all learning data in parallel
    const [
      curriculumEnrollmentsResult,
      courseEnrollmentsResult,
      curriculumAssignmentsResult,
      courseAssignmentsResult,
      certificatesResult,
      curriculumCoursesResult,
      modulesResult,
      moduleProgressResult,
    ] = await Promise.all([
      supabase.from("curriculum_enrollments").select("id, user_id, curriculum_id, enrolled_at, completed_at"),
      supabase.from("course_enrollments").select("id, user_id, course_id, enrolled_at, completed_at"),
      supabase.from("curriculum_assignments").select("id, user_id, curriculum_id, completed_at"),
      supabase.from("course_assignments").select("id, user_id, course_id, completed_at"),
      supabase.from("certificates").select("id, user_id, curriculum_id"),
      supabase.from("curriculum_courses").select("curriculum_id, course_id"),
      supabase.from("modules").select("id, course_id"),
      supabase.from("module_progress").select("id, module_id, enrollment_id, completed_at"),
    ]);

    // Build lookup maps
    const curriculumCourses = curriculumCoursesResult.data || [];
    const modules = modulesResult.data || [];
    const moduleProgress = moduleProgressResult.data || [];
    const courseEnrollments = courseEnrollmentsResult.data || [];

    // Get learners (users with any enrollment)
    const learnerIds = new Set<string>();
    (curriculumEnrollmentsResult.data || []).forEach((e) => learnerIds.add(e.user_id));
    (courseEnrollmentsResult.data || []).forEach((e) => learnerIds.add(e.user_id));

    const rows: AllLearnersExportRow[] = [];

    for (const learnerId of learnerIds) {
      const profile = profiles?.find((p) => p.id === learnerId);
      if (!profile) continue;

      // Curriculum enrollments for this learner
      const learnerCurriculumEnrollments = (curriculumEnrollmentsResult.data || []).filter(
        (e) => e.user_id === learnerId
      );
      const completedCurriculumEnrollments = learnerCurriculumEnrollments.filter((e) => e.completed_at);

      // Course enrollments for this learner
      const learnerCourseEnrollments = (courseEnrollmentsResult.data || []).filter(
        (e) => e.user_id === learnerId
      );
      const completedCourseEnrollments = learnerCourseEnrollments.filter((e) => e.completed_at);

      // Calculate curriculum progress
      let totalCurriculumProgress = 0;
      learnerCurriculumEnrollments.forEach((enrollment) => {
        const coursesInCurriculum = curriculumCourses.filter(
          (cc) => cc.curriculum_id === enrollment.curriculum_id
        );
        const completedCourses = learnerCourseEnrollments.filter(
          (ce) =>
            ce.completed_at &&
            coursesInCurriculum.some((cc) => cc.course_id === ce.course_id)
        ).length;
        const progress =
          coursesInCurriculum.length > 0
            ? (completedCourses / coursesInCurriculum.length) * 100
            : 0;
        totalCurriculumProgress += progress;
      });
      const avgCurriculumProgress =
        learnerCurriculumEnrollments.length > 0
          ? Math.round(totalCurriculumProgress / learnerCurriculumEnrollments.length)
          : 0;

      // Calculate course progress
      let totalCourseProgress = 0;
      learnerCourseEnrollments.forEach((enrollment) => {
        const modulesInCourse = modules.filter((m) => m.course_id === enrollment.course_id);
        const completedModules = moduleProgress.filter(
          (mp) =>
            mp.completed_at &&
            mp.enrollment_id === enrollment.id &&
            modulesInCourse.some((m) => m.id === mp.module_id)
        ).length;
        const progress =
          modulesInCourse.length > 0
            ? (completedModules / modulesInCourse.length) * 100
            : 0;
        totalCourseProgress += progress;
      });
      const avgCourseProgress =
        learnerCourseEnrollments.length > 0
          ? Math.round(totalCourseProgress / learnerCourseEnrollments.length)
          : 0;

      // Certificates
      const learnerCertificates = (certificatesResult.data || []).filter(
        (c) => c.user_id === learnerId
      );

      // Assignments
      const learnerCurriculumAssignments = (curriculumAssignmentsResult.data || []).filter(
        (a) => a.user_id === learnerId
      );
      const learnerCourseAssignments = (courseAssignmentsResult.data || []).filter(
        (a) => a.user_id === learnerId
      );
      const allAssignments = [...learnerCurriculumAssignments, ...learnerCourseAssignments];
      const completedAssignments = allAssignments.filter((a) => a.completed_at);
      const pendingAssignments = allAssignments.length - completedAssignments.length;

      // Last activity date (most recent enrollment or completion)
      const dates: Date[] = [];
      learnerCurriculumEnrollments.forEach((e) => {
        dates.push(new Date(e.enrolled_at));
        if (e.completed_at) dates.push(new Date(e.completed_at));
      });
      learnerCourseEnrollments.forEach((e) => {
        dates.push(new Date(e.enrolled_at));
        if (e.completed_at) dates.push(new Date(e.completed_at));
      });
      const lastActivity = dates.length > 0 ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;

      rows.push({
        "Learner Name": profile.full_name || "Unknown",
        Email: profile.email || "N/A",
        "User Type": profile.role || "N/A",
        "Curricula Enrolled": learnerCurriculumEnrollments.length,
        "Curricula Completed": completedCurriculumEnrollments.length,
        "Courses Enrolled": learnerCourseEnrollments.length,
        "Courses Completed": completedCourseEnrollments.length,
        "Avg Curriculum Progress %": `${avgCurriculumProgress}%`,
        "Avg Course Progress %": `${avgCourseProgress}%`,
        "Certificates Earned": learnerCertificates.length,
        "Pending Assignments": pendingAssignments,
        "Completed Assignments": completedAssignments.length,
        "Last Activity Date": lastActivity ? format(lastActivity, "yyyy-MM-dd") : "N/A",
      });
    }

    if (rows.length === 0) {
      toast.error("No learner data to export");
      return;
    }

    // Sort by name
    rows.sort((a, b) => a["Learner Name"].localeCompare(b["Learner Name"]));

    // Generate CSV
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `all_learners_summary_${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${rows.length} learners!`);
  } catch (error) {
    console.error("Export all learners error:", error);
    toast.error("Failed to export learner data");
  }
}
